/**
 * API Configuration
 * Base URL for the Vedic API
 */
const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Generic API error handler
 */
class ApiError extends Error {
  constructor(message, status, errors = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
  }
}

/**
 * Get auth token from localStorage
 */
function getAuthToken() {
  return localStorage.getItem('vedic_ai_token');
}

/**
 * Generic fetch wrapper with error handling
 */
// Keep track of ongoing refresh token promise to handle concurrent requests
let refreshPromise = null;

async function apiFetch(url, options = {}) {
  try {
    let token = getAuthToken();
    const headers = {};

    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response = await fetch(url, {
      ...options,
      headers,
    });

    // Check for 401 Unauthorized
    if (response.status === 401) {
      const refreshToken = localStorage.getItem('vedic_ai_refresh_token');
      if (token && refreshToken) {
        try {
          // If a refresh is already in progress, wait for it
          if (!refreshPromise) {
            refreshPromise = fetch(`${API_BASE_URL}/Auth/refresh-token`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ accessToken: token, refreshToken }),
            }).then(async (res) => {
              if (!res.ok) throw new Error('Failed to refresh token');
              const refreshData = await res.json();
              return refreshData.data;
            }).finally(() => {
              refreshPromise = null;
            });
          }

          const refreshResult = await refreshPromise;
          if (refreshResult && refreshResult.token) {
            localStorage.setItem('vedic_ai_token', refreshResult.token);
            localStorage.setItem('vedic_ai_refresh_token', refreshResult.refreshToken);

            // Update authorization header and retry original request
            headers['Authorization'] = `Bearer ${refreshResult.token}`;
            response = await fetch(url, {
              ...options,
              headers,
            });
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          localStorage.removeItem('vedic_ai_user');
          localStorage.removeItem('vedic_ai_token');
          localStorage.removeItem('vedic_ai_refresh_token');
          window.dispatchEvent(new CustomEvent('unauthorized'));

          throw new ApiError(
            'Session expired. Please log in again.',
            401,
            [refreshError.message]
          );
        }
      }
    }

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        data.message || 'An error occurred',
        response.status,
        data.errors || []
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError('Network error occurred', 0, [error.message]);
  }
}

/**
 * API Service
 * Centralized service for all API calls
 */
const apiService = {
  API_BASE_URL,
  /**
   * Get all books
   */
  getAllBooks: async () => {
    const response = await apiFetch(`${API_BASE_URL}/books`);
    return response.data;
  },

  /**
   * Get active books only
   */
  getActiveBooks: async () => {
    const response = await apiFetch(`${API_BASE_URL}/books/active`);
    return response.data;
  },

  /**
   * Get book by ID
   */
  getBookById: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/books/${id}`);
    return response.data;
  },

  /**
   * Get books by category
   */
  getBooksByCategory: async (category) => {
    const response = await apiFetch(`${API_BASE_URL}/books/category/${encodeURIComponent(category)}`);
    return response.data;
  },

  /**
   * Search books
   */
  searchBooks: async (searchTerm) => {
    const response = await apiFetch(`${API_BASE_URL}/books/search?searchTerm=${encodeURIComponent(searchTerm)}`);
    return response.data;
  },

  /**
   * Create a new book
   */
  createBook: async (bookData) => {
    const response = await apiFetch(`${API_BASE_URL}/books`, {
      method: 'POST',
      body: JSON.stringify(bookData),
    });
    return response.data;
  },

  /**
   * Update an existing book
   */
  updateBook: async (id, bookData) => {
    const response = await apiFetch(`${API_BASE_URL}/books/${id}`, {
      method: 'PUT',
      body: JSON.stringify(bookData),
    });
    return response.data;
  },

  /**
   * Delete a book
   */
  deleteBook: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/books/${id}`, {
      method: 'DELETE',
    });
    return response.data;
  },

  // ==================== Chapter API Methods ====================

  /**
   * Get book with all chapters (summaries only)
   */
  getBookWithChapters: async (bookId) => {
    const response = await apiFetch(`${API_BASE_URL}/books/${bookId}/chapters`);
    return response.data;
  },

  /**
   * Get specific chapter by chapter number
   */
  getChapter: async (bookId, chapterNumber) => {
    const response = await apiFetch(`${API_BASE_URL}/books/${bookId}/chapters/${chapterNumber}`);
    return response.data;
  },

  /**
   * Get specific chapter by unique indexable slug
   */
  getChapterBySlug: async (slug) => {
    const response = await apiFetch(`${API_BASE_URL}/chapters/by-slug/${slug}`);
    return response.data;
  },

  /**
   * Get next chapter
   */
  getNextChapter: async (bookId, chapterNumber) => {
    const response = await apiFetch(`${API_BASE_URL}/books/${bookId}/chapters/${chapterNumber}/next`);
    return response.data;
  },

  /**
   * Get previous chapter
   */
  getPreviousChapter: async (bookId, chapterNumber) => {
    const response = await apiFetch(`${API_BASE_URL}/books/${bookId}/chapters/${chapterNumber}/previous`);
    return response.data;
  },

  /**
   * Create a new chapter for a book
   */
  createChapter: async (bookId, chapterData) => {
    const response = await apiFetch(`${API_BASE_URL}/chapters/books/${bookId}`, {
      method: 'POST',
      body: JSON.stringify(chapterData),
    });
    return response.data;
  },

  /**
   * Update an existing chapter
   */
  updateChapter: async (chapterId, chapterData) => {
    const response = await apiFetch(`${API_BASE_URL}/chapters/${chapterId}`, {
      method: 'PUT',
      body: JSON.stringify(chapterData),
    });
    return response.data;
  },

  /**
   * Delete a chapter
   */
  deleteChapter: async (chapterId) => {
    const response = await apiFetch(`${API_BASE_URL}/chapters/${chapterId}`, {
      method: 'DELETE',
    });
    return response.data;
  },

  /**
   * Get chapter by ID
   */
  getChapterById: async (chapterId) => {
    const response = await apiFetch(`${API_BASE_URL}/chapters/${chapterId}`);
    return response.data;
  },

  // ==================== Authentication API Methods ====================

  /**
   * User signup/registration
   */
  signup: async (name, email, password, role) => {
    const response = await apiFetch(`${API_BASE_URL}/Auth/signup`, {
      method: 'POST',
      body: JSON.stringify({ name, email, password, role }),
    });
    return response;
  },

  /**
   * User login
   */
  login: async (email, password) => {
    const response = await apiFetch(`${API_BASE_URL}/Auth/login`, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    return response;
  },

  /**
   * Refresh access token
   */
  refreshToken: async (accessToken, refreshToken) => {
    const response = await apiFetch(`${API_BASE_URL}/Auth/refresh-token`, {
      method: 'POST',
      body: JSON.stringify({ accessToken, refreshToken }),
    });
    return response;
  },

  /**
   * Logout user (revoke refresh token)
   */
  logout: async () => {
    const response = await apiFetch(`${API_BASE_URL}/Auth/logout`, {
      method: 'POST',
    });
    return response;
  },

  /**
   * Get user profile
   */
  getUserProfile: async () => {
    const response = await apiFetch(`${API_BASE_URL}/Auth/profile`);
    return response.data;
  },

  /**
   * Check if email is available
   */
  checkEmailAvailability: async (email) => {
    const response = await apiFetch(`${API_BASE_URL}/Auth/check-email?email=${encodeURIComponent(email)}`);
    return response.data;
  },

  /**
   * Forgot password - Request OTP
   */
  forgotPassword: async (email) => {
    const response = await apiFetch(`${API_BASE_URL}/Auth/forgot-password`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return response;
  },

  /**
   * Verify OTP for password reset
   */
  verifyOTP: async (email, otp) => {
    const response = await apiFetch(`${API_BASE_URL}/Auth/verify-otp`, {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    });
    return response;
  },

  /**
   * Reset password with OTP
   */
  resetPassword: async (email, otp, newPassword) => {
    const response = await apiFetch(`${API_BASE_URL}/Auth/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ email, otp, newPassword }),
    });
    return response;
  },

  /**
   * Update user profile (Name, Email)
   */
  updateUserProfile: async (profileData) => {
    const response = await apiFetch(`${API_BASE_URL}/Auth/profile`, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
    return response.data;
  },

  /**
   * Upload profile image
   */
  uploadProfileImage: async (formData) => {
    const response = await apiFetch(`${API_BASE_URL}/Auth/profile/image`, {
      method: 'POST',
      body: formData,
    });
    return response.data;
  },

  /**
   * Delete profile image
   */
  deleteProfileImage: async () => {
    const response = await apiFetch(`${API_BASE_URL}/Auth/profile/image`, {
      method: 'DELETE',
    });
    return response.data;
  },

  // ==========================================
  // RESEARCH PAPERS API
  // ==========================================

  /**
   * Get all research papers
   */
  getAllResearchPapers: async () => {
    const response = await apiFetch(`${API_BASE_URL}/ResearchPapers`);
    return response.data;
  },

  /**
   * Get featured research papers
   */
  getFeaturedResearchPapers: async (count = 3) => {
    const response = await apiFetch(`${API_BASE_URL}/ResearchPapers/featured?count=${count}`);
    return response.data;
  },

  /**
   * Get research paper by ID
   */
  getResearchPaperById: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/ResearchPapers/${id}`);
    return response.data;
  },

  /**
   * Get research papers by category
   */
  getResearchPapersByCategory: async (category) => {
    const response = await apiFetch(`${API_BASE_URL}/ResearchPapers/category/${encodeURIComponent(category)}`);
    return response.data;
  },

  /**
   * Get research papers by year
   */
  getResearchPapersByYear: async (year) => {
    const response = await apiFetch(`${API_BASE_URL}/ResearchPapers/year/${year}`);
    return response.data;
  },

  /**
   * Get research papers by institution
   */
  getResearchPapersByInstitution: async (institution) => {
    const response = await apiFetch(`${API_BASE_URL}/ResearchPapers/institution/${encodeURIComponent(institution)}`);
    return response.data;
  },

  /**
   * Search research papers
   */
  searchResearchPapers: async (searchTerm) => {
    const response = await apiFetch(`${API_BASE_URL}/ResearchPapers/search?searchTerm=${encodeURIComponent(searchTerm)}`);
    return response.data;
  },

  /**
   * Increment research paper download count
   */
  incrementResearchPaperDownload: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/ResearchPapers/${id}/download`, {
      method: 'POST',
    });
    return response.data;
  },

  // ==========================================
  // THESIS API
  // ==========================================

  /**
   * Get all thesis
   */
  getAllThesis: async () => {
    const response = await apiFetch(`${API_BASE_URL}/Thesis`);
    return response.data;
  },

  /**
   * Get featured thesis
   */
  getFeaturedThesis: async (count = 3) => {
    const response = await apiFetch(`${API_BASE_URL}/Thesis/featured?count=${count}`);
    return response.data;
  },

  /**
   * Get thesis by ID
   */
  getThesisById: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/Thesis/${id}`);
    return response.data;
  },

  /**
   * Get thesis by category
   */
  getThesisByCategory: async (category) => {
    const response = await apiFetch(`${API_BASE_URL}/Thesis/category/${encodeURIComponent(category)}`);
    return response.data;
  },

  /**
   * Get thesis by year
   */
  getThesisByYear: async (year) => {
    const response = await apiFetch(`${API_BASE_URL}/Thesis/year/${year}`);
    return response.data;
  },

  /**
   * Get thesis by institution
   */
  getThesisByInstitution: async (institution) => {
    const response = await apiFetch(`${API_BASE_URL}/Thesis/institution/${encodeURIComponent(institution)}`);
    return response.data;
  },

  /**
   * Search thesis
   */
  searchThesis: async (searchTerm) => {
    const response = await apiFetch(`${API_BASE_URL}/Thesis/search?searchTerm=${encodeURIComponent(searchTerm)}`);
    return response.data;
  },

  /**
   * Increment thesis download count
   */
  incrementThesisDownload: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/Thesis/${id}/download`, {
      method: 'POST',
    });
    return response.data;
  },

  // ==========================================
  // CONTACT API
  // ==========================================

  /**
   * Submit contact form
   */
  submitContactForm: async (contactData) => {
    const response = await apiFetch(`${API_BASE_URL}/Contact/submit`, {
      method: 'POST',
      body: JSON.stringify(contactData),
    });
    return response;
  },

  // ==========================================
  // NEWSLETTER API
  // ==========================================

  /**
   * Subscribe to newsletter
   */
  subscribeNewsletter: async (email) => {
    const response = await apiFetch(`${API_BASE_URL}/Newsletter/subscribe`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return response;
  },

  // ==========================================
  // TEXTBOOKS API
  // ==========================================

  /**
   * Get all textbooks
   */
  getAllTextbooks: async () => {
    const response = await apiFetch(`${API_BASE_URL}/Textbooks`);
    return response.data;
  },

  /**
   * Get active textbooks only
   */
  getActiveTextbooks: async () => {
    const response = await apiFetch(`${API_BASE_URL}/Textbooks/active`);
    return response.data;
  },

  /**
   * Get textbook by ID
   */
  getTextbookById: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/Textbooks/${id}`);
    return response.data;
  },

  /**
   * Get textbook with all its chapters
   */
  getTextbookWithChapters: async (textbookId) => {
    const response = await apiFetch(`${API_BASE_URL}/Textbooks/${textbookId}/chapters`);
    return response.data;
  },

  /**
   * Get textbooks by category
   */
  getTextbooksByCategory: async (category) => {
    const response = await apiFetch(`${API_BASE_URL}/Textbooks/category/${encodeURIComponent(category)}`);
    return response.data;
  },

  /**
   * Get textbooks by level
   */
  getTextbooksByLevel: async (level) => {
    const response = await apiFetch(`${API_BASE_URL}/Textbooks/level/${encodeURIComponent(level)}`);
    return response.data;
  },

  /**
   * Search textbooks
   */
  searchTextbooks: async (searchTerm) => {
    const response = await apiFetch(`${API_BASE_URL}/Textbooks/search?searchTerm=${encodeURIComponent(searchTerm)}`);
    return response.data;
  },

  /**
   * Get textbook chapter by ID
   */
  getTextbookChapterById: async (chapterId) => {
    const response = await apiFetch(`${API_BASE_URL}/TextbookChapters/${chapterId}`);
    return response.data;
  },

  // ==========================================
  // TREATMENT / AYUSH API
  // ==========================================

  // Patients
  getPatients: async () => {
    const response = await apiFetch(`${API_BASE_URL}/patients`);
    return response.data;
  },

  getPatientById: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/patients/${id}`);
    return response.data;
  },

  getPatientByUserId: async (userId) => {
    const response = await apiFetch(`${API_BASE_URL}/patients/user/${userId}`);
    return response.data;
  },

  createPatient: async (body) => {
    const response = await apiFetch(`${API_BASE_URL}/patients`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.data;
  },

  updatePatient: async (id, body) => {
    const response = await apiFetch(`${API_BASE_URL}/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return response.data;
  },

  deletePatient: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/patients/${id}`, {
      method: 'DELETE',
    });
    return response.data;
  },

  // Prakriti
  getPrakritiQuestions: async () => {
    const response = await apiFetch(`${API_BASE_URL}/prakriti/questions`);
    return response.data;
  },

  submitPrakritiAssessment: async (body) => {
    const response = await apiFetch(`${API_BASE_URL}/prakriti/assess`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.data;
  },

  getPrakritiAssessmentById: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/prakriti/assessment/${id}`);
    return response.data;
  },

  getLatestPrakritiAssessment: async (patientId) => {
    const response = await apiFetch(`${API_BASE_URL}/prakriti/patient/${patientId}/latest`);
    return response.data;
  },

  // AI Status & Feature Flags
  getAiStatus: async () => {
    const response = await apiFetch(`${API_BASE_URL}/treatment-recommendations/ai-status`);
    return response.data;
  },

  // Recommendations
  generateRecommendations: async (patientId, conditionId, useAi = true, customClinicalNotes = '', customConditionName = null) => {
    const params = new URLSearchParams({ 
      patientId: String(patientId), 
      conditionId: String(conditionId),
      useAi: String(useAi)
    });
    const response = await apiFetch(`${API_BASE_URL}/treatment-recommendations/generate?${params}`, {
      method: 'POST',
      body: JSON.stringify({ customClinicalNotes, customConditionName }),
    });
    return response.data;
  },

  suggestAdjustment: async (patientId, conditionId, currentPlan, outcomes) => {
    const response = await apiFetch(`${API_BASE_URL}/treatment-recommendations/suggest-adjustment`, {
      method: 'POST',
      body: JSON.stringify({ patientId, conditionId, currentPlan, outcomes }),
    });
    return response.data;
  },

  searchConditions: async (searchTerm, category = null) => {
    const params = new URLSearchParams({ searchTerm });
    if (category) params.set('category', category);
    const response = await apiFetch(`${API_BASE_URL}/treatment-recommendations/conditions/search?${params}`);
    return response.data;
  },

  getHerbalMedicines: async (searchTerm = null, prakritiEffect = null) => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('searchTerm', searchTerm);
    if (prakritiEffect) params.set('prakritiEffect', prakritiEffect);
    const qs = params.toString();
    const response = await apiFetch(`${API_BASE_URL}/treatment-recommendations/medicines${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  getYogaAsanas: async (category = null, difficulty = null, prakritiEffect = null) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (difficulty) params.set('difficulty', difficulty);
    if (prakritiEffect) params.set('prakritiEffect', prakritiEffect);
    const qs = params.toString();
    const response = await apiFetch(`${API_BASE_URL}/treatment-recommendations/yoga${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  getDietaryItems: async (prakriti, category = null) => {
    const params = new URLSearchParams({ prakriti });
    if (category) params.set('category', category);
    const response = await apiFetch(`${API_BASE_URL}/treatment-recommendations/dietary?${params}`);
    return response.data;
  },

  // Treatment plans
  getTreatmentPlanById: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/treatment-plans/${id}`);
    return response.data;
  },

  getTreatmentPlansByPatientId: async (patientId) => {
    const response = await apiFetch(`${API_BASE_URL}/treatment-plans/patient/${patientId}`);
    return response.data;
  },

  createTreatmentPlan: async (body) => {
    const response = await apiFetch(`${API_BASE_URL}/treatment-plans`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.data;
  },

  updateTreatmentPlan: async (id, body) => {
    const response = await apiFetch(`${API_BASE_URL}/treatment-plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return response.data;
  },

  submitTreatmentOutcome: async (planId, body) => {
    const response = await apiFetch(`${API_BASE_URL}/treatment-plans/${planId}/outcome`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.data;
  },

  // Catalog
  getCatalogMedicines: async (searchTerm = null) => {
    const qs = searchTerm ? `?searchTerm=${encodeURIComponent(searchTerm)}` : '';
    const response = await apiFetch(`${API_BASE_URL}/catalog/medicines${qs}`);
    return response.data;
  },

  getCatalogMedicineById: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/catalog/medicines/${id}`);
    return response.data;
  },

  getCatalogYoga: async (category = null, difficulty = null) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (difficulty) params.set('difficulty', difficulty);
    const qs = params.toString();
    const response = await apiFetch(`${API_BASE_URL}/catalog/yoga${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  getCatalogYogaById: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/catalog/yoga/${id}`);
    return response.data;
  },

  getCatalogDietary: async (prakriti, category = null) => {
    const params = new URLSearchParams({ prakriti });
    if (category) params.set('category', category);
    const response = await apiFetch(`${API_BASE_URL}/catalog/dietary?${params}`);
    return response.data;
  },

  getCatalogConditions: async (searchTerm = null, category = null) => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('searchTerm', searchTerm);
    if (category) params.set('category', category);
    const qs = params.toString();
    const response = await apiFetch(`${API_BASE_URL}/catalog/conditions${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  getCatalogStatistics: async () => {
    const response = await apiFetch(`${API_BASE_URL}/catalog/statistics`);
    return response.data;
  },

  // ==========================================
  // JURISPRUDENCE API
  // ==========================================

  /**
   * Get paged jurisprudence items
   */
  getJurisprudencePage: async ({ page = 1, pageSize = 10, searchTerm = null, type = null }) => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('pageSize', pageSize.toString());
    if (searchTerm) params.set('searchTerm', searchTerm);
    if (type) params.set('type', type);
    const response = await apiFetch(`${API_BASE_URL}/jurisprudence?${params}`);
    return response.data;
  },

  /**
   * Get jurisprudence item by ID
   */
  getJurisprudenceById: async (id) => {
    const response = await apiFetch(`${API_BASE_URL}/jurisprudence/${id}`);
    return response.data;
  },

  analyzeReaderText: async (text, action) => {
    const response = await apiFetch(`${API_BASE_URL}/reader-ai/analyze`, {
      method: 'POST',
      body: JSON.stringify({ text, action }),
    });
    return response.data;
  },

  /**
   * Continue a textbook reader AI chat discussion with previous history and follow-up query
   */
  chatReaderText: async (text, action, history, message) => {
    const response = await apiFetch(`${API_BASE_URL}/reader-ai/chat`, {
      method: 'POST',
      body: JSON.stringify({ text, action, history, message }),
    });
    return response.data;
  },

  // ==========================================
  // WORKSPACE HUB API
  // ==========================================

  /**
   * Get paginated workspace community posts
   */
  getWorkspacePosts: async (page = 1, limit = 10) => {
    const response = await apiFetch(`${API_BASE_URL}/workspace/posts?page=${page}&limit=${limit}`);
    return response.data;
  },

  /**
   * Create a new workspace community post
   */
  createWorkspacePost: async (postData) => {
    const response = await apiFetch(`${API_BASE_URL}/workspace/posts`, {
      method: 'POST',
      body: JSON.stringify(postData),
    });
    return response.data;
  },

  /**
   * Toggle a like on a workspace community post
   */
  toggleLikePost: async (postId) => {
    const response = await apiFetch(`${API_BASE_URL}/workspace/posts/${postId}/like`, {
      method: 'POST',
    });
    return response.data;
  },

  /**
   * Get daily Sushruta Samhita wisdom quote
   */
  getDailyWisdom: async () => {
    const response = await apiFetch(`${API_BASE_URL}/workspace/wisdom`);
    return response.data;
  },

  /**
   * Get workspace discussion categories
   */
  getWorkspaceCategories: async () => {
    const response = await apiFetch(`${API_BASE_URL}/workspace/categories`);
    return response.data;
  },

  /**
   * Get active AYUSH Clinical Suite modules
   */
  getClinicalSuiteModules: async () => {
    const response = await apiFetch(`${API_BASE_URL}/workspace/clinical-suite`);
    return response.data;
  },

  /**
   * Get trending surgical tags
   */
  getWorkspaceTags: async () => {
    const response = await apiFetch(`${API_BASE_URL}/workspace/tags`);
    return response.data;
  },

  // ==========================================
  // SEO SLUG API METHODS
  // ==========================================

  /**
   * Get textbook chapter details by slug
   */
  getTextbookChapterBySlug: async (slug) => {
    const response = await apiFetch(`${API_BASE_URL}/TextbookChapters/by-slug/${slug}`);
    return response.data;
  },

  /**
   * Get article details by slug
   */
  getArticleBySlug: async (slug) => {
    const response = await apiFetch(`${API_BASE_URL}/Articles/by-slug/${slug}`);
    return response.data;
  },

  /**
   * Get public article feed
   */
  getArticleFeed: async (category = null, page = 1, limit = 10) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    params.set('page', page.toString());
    params.set('limit', limit.toString());
    const response = await apiFetch(`${API_BASE_URL}/Articles/feed?${params}`);
    return response.data;
  },

  /**
   * Get public research paper feed
   */
  getResearchFeed: async ({ category = null, page = 1, limit = 10, search = null, year = null, institution = null } = {}) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (search) params.set('search', search);
    if (year) params.set('year', year.toString());
    if (institution) params.set('institution', institution);
    params.set('page', page.toString());
    params.set('limit', limit.toString());
    const response = await apiFetch(`${API_BASE_URL}/ResearchPapers/feed?${params}`);
    return response.data;
  },

  /**
   * Get research paper by slug
   */
  getResearchBySlug: async (slug) => {
    const response = await apiFetch(`${API_BASE_URL}/ResearchPapers/by-slug/${slug}`);
    return response.data;
  },

  /**
   * Get public thesis feed
   */
  getThesisFeed: async ({ category = null, page = 1, limit = 10, search = null, year = null, institution = null } = {}) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (search) params.set('search', search);
    if (year) params.set('year', year.toString());
    if (institution) params.set('institution', institution);
    params.set('page', page.toString());
    params.set('limit', limit.toString());
    const response = await apiFetch(`${API_BASE_URL}/Thesis/feed?${params}`);
    return response.data;
  },

  /**
   * Get thesis by slug
   */
  getThesisBySlug: async (slug) => {
    const response = await apiFetch(`${API_BASE_URL}/Thesis/by-slug/${slug}`);
    return response.data;
  },
};

export default apiService;
export { ApiError };

